import { computed, Injectable, inject, signal } from '@angular/core';
import { firstValueFrom, of, catchError, map, switchMap, tap } from 'rxjs';
import { API_CONFIG } from '@core/config/api.config';
import { LoginPayload, StoredSession, TaxiUser } from './models/taxi-user.model';
import { collectPermisos, permisosFromJwt } from './permission.util';
import {
  AutenticacionResponse,
  AutenticacionService,
  PerfilResponse
} from '@service/autenticacion.service';
import { httpErrorMessage } from '@service/api-response.model';

const MAX_FAILED_ATTEMPTS = 5;
const BLOCKED_EMAIL = 'bloqueado@taxiadmin.mx';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(AutenticacionService);
  private readonly storageKey = 'taxi-admin-session';
  private readonly attemptsKey = 'taxi-admin-failed-attempts';
  private readonly _user = signal<TaxiUser | null>(this.readSession()?.user ?? null);
  private readonly _token = signal<string | null>(this.readSession()?.token ?? null);
  private refreshInFlight: Promise<string | null> | null = null;
  /** Evita refrescar de más al encadenar login → boot → app. */
  private lastRefreshAt = 0;
  private readonly refreshTtlMs = 30_000;

  readonly user = this._user.asReadonly();
  readonly isLoggedIn = computed(() => Boolean(this._user() && this._token()));

  login(payload: LoginPayload): Promise<TaxiUser> {
    if (API_CONFIG.useMockAuth) {
      return this.mockLogin(payload);
    }

    const correo = payload.email.trim().toLowerCase();
    const password = payload.password;

    return firstValueFrom(
      this.api.login({ correo, password }).pipe(
        switchMap((auth) => {
          const provisional = mapAuthToUser(auth);
          this.persistSession(provisional, auth.tokenJWT);
          return this.api.refrescarToken(auth.tokenJWT).pipe(
            catchError(() => of(auth.tokenJWT)),
            tap((token) => {
              this.updateToken(token);
              this.lastRefreshAt = Date.now();
            }),
            switchMap((token) =>
              this.api.obtenerPerfil(token).pipe(
                tap((perfil) => {
                  const user = mapPerfilToUser(perfil, auth, this._user());
                  this.persistSession(user, token);
                }),
                map(() => this._user()!),
                catchError(() => of(provisional))
              )
            )
          );
        }),
        catchError((err) => {
          throw new Error(
            httpErrorMessage(err, 'Correo o contraseña incorrectos. Verifica tus datos e intenta de nuevo.')
          );
        })
      )
    );
  }

  /**
   * Renueva el JWT. Usado al boot y ante 401.
   * Deduplica llamadas concurrentes y respeta un TTL corto.
   */
  refreshSession(options?: { force?: boolean }): Promise<string | null> {
    if (API_CONFIG.useMockAuth) {
      return Promise.resolve(this.getToken());
    }

    const current = this.getToken();
    if (!current) {
      return Promise.resolve(null);
    }

    const force = options?.force === true;
    if (!force && this.lastRefreshAt && Date.now() - this.lastRefreshAt < this.refreshTtlMs) {
      return Promise.resolve(current);
    }

    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = firstValueFrom(
      this.api.refrescarToken(current).pipe(
        tap((token) => {
          this.updateToken(token);
          this.lastRefreshAt = Date.now();
        }),
        catchError(() => of(null))
      )
    ).finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  /** Carga /usuarios/me y actualiza la sesión local. */
  loadProfile(): Promise<TaxiUser | null> {
    if (API_CONFIG.useMockAuth) {
      return Promise.resolve(this._user());
    }

    const token = this.getToken();
    if (!token) {
      return Promise.resolve(null);
    }

    return firstValueFrom(
      this.api.obtenerPerfil(token).pipe(
        map((perfil) => {
          const user = mapPerfilToUser(perfil, undefined, this._user());
          this.persistSession(user, token);
          return user;
        }),
        catchError(() => of(this._user()))
      )
    );
  }

  /**
   * Al reabrir la app con sesión: refresca token y perfil.
   * Si el refresh falla, limpia sesión.
   */
  hydrateSession(): Promise<boolean> {
    if (API_CONFIG.useMockAuth) {
      return Promise.resolve(this.isLoggedIn());
    }

    if (!this.getToken()) {
      return Promise.resolve(false);
    }

    return this.refreshSession().then(async (token) => {
      if (!token) {
        this.clearLocalSession();
        return false;
      }
      await this.loadProfile();
      return this.isLoggedIn();
    });
  }

  requestPasswordReset(email: string): Promise<void> {
    if (API_CONFIG.useMockAuth) {
      return new Promise((resolve) => setTimeout(() => resolve(), 600));
    }

    return firstValueFrom(
      this.api.solicitarRecuperacion({ correo: email.trim().toLowerCase() }).pipe(
        catchError((err) => {
          throw new Error(httpErrorMessage(err, 'No fue posible enviar el correo. Intenta más tarde.'));
        })
      )
    );
  }

  resetPassword(token: string, password: string): Promise<void> {
    if (API_CONFIG.useMockAuth) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (token === 'expired') {
            reject(new Error('Este enlace ya no es válido. Solicita uno nuevo.'));
            return;
          }
          resolve();
        }, 600);
      });
    }

    return firstValueFrom(
      this.api.restablecerPassword({ token, nuevoPassword: password }).pipe(
        catchError((err) => {
          throw new Error(
            httpErrorMessage(err, 'Ocurrió un error al guardar la contraseña. Intenta de nuevo.')
          );
        })
      )
    );
  }

  /** Activa la cuenta con el token del correo y la contraseña elegida. */
  activateAccount(token: string, password: string): Promise<string> {
    if (API_CONFIG.useMockAuth) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (!token || token === 'expired') {
            reject(new Error('Este enlace de activación ya no es válido.'));
            return;
          }
          if (!password) {
            reject(new Error('La contraseña es requerida.'));
            return;
          }
          resolve('Tu cuenta ha sido activada correctamente.');
        }, 700);
      });
    }

    return firstValueFrom(
      this.api.activarUsuario(token, password).pipe(
        catchError((err) => {
          throw new Error(
            httpErrorMessage(err, 'No se pudo activar la cuenta. El enlace puede haber expirado.')
          );
        })
      )
    );
  }

  /** Invalida token en servidor (best effort) y limpia sesión local. */
  async logout(): Promise<void> {
    const token = this.getToken();
    if (!API_CONFIG.useMockAuth && token) {
      try {
        await firstValueFrom(this.api.eliminarToken(token).pipe(catchError(() => of(null))));
      } catch {
        // best effort
      }
    }
    this.clearLocalSession();
  }

  /** Token JWT de la sesión actual (si existe). */
  getToken(): string | null {
    return this._token() ?? this.readSession()?.token ?? null;
  }

  /** Actualiza solo el token (p. ej. tras refresh). */
  updateToken(token: string): void {
    const user = this._user() ?? this.readSession()?.user;
    if (!user) {
      this._token.set(token);
      return;
    }
    const fromJwt = permisosFromJwt(token);
    const next: TaxiUser = fromJwt.length > 0 ? { ...user, permisos: fromJwt } : user;
    this.persistSession(next, token);
  }

  clearLocalSession(): void {
    localStorage.removeItem(this.storageKey);
    this._user.set(null);
    this._token.set(null);
    this.lastRefreshAt = 0;
  }

  private mockLogin(payload: LoginPayload): Promise<TaxiUser> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const email = payload.email.trim().toLowerCase();
        const password = payload.password;

        if (!email || !password) {
          reject(new Error('Correo o contraseña incorrectos. Verifica tus datos e intenta de nuevo.'));
          return;
        }

        if (email === BLOCKED_EMAIL || this.getFailedAttempts(email) >= MAX_FAILED_ATTEMPTS) {
          reject(
            new Error(
              'Tu cuenta está bloqueada. Contacta al administrador del sistema para desbloquearla.'
            )
          );
          return;
        }

        const validDemo =
          (email === 'admin@taxiadmin.mx' && password === 'admin123') ||
          (password.length >= 4 && email !== 'fail@taxiadmin.mx');

        if (!validDemo || email === 'fail@taxiadmin.mx') {
          const attempts = this.incrementFailedAttempts(email);
          if (attempts >= MAX_FAILED_ATTEMPTS) {
            reject(
              new Error(
                'Tu cuenta ha sido bloqueada por múltiples intentos fallidos. Contacta al administrador del sistema para desbloquearla.'
              )
            );
            return;
          }

          reject(new Error('Correo o contraseña incorrectos. Verifica tus datos e intenta de nuevo.'));
          return;
        }

        this.clearFailedAttempts(email);

        const user: TaxiUser = {
          name: email === 'admin@taxiadmin.mx' ? 'Admin Demo' : email.split('@')[0],
          email,
          role: 'Superadministrador',
          avatar: email.slice(0, 2).toUpperCase(),
          permisos: []
        };

        this.persistSession(user, `mock-token-${Date.now()}`);
        resolve(user);
      }, 650);
    });
  }

  private persistSession(user: TaxiUser, token: string): void {
    const session: StoredSession = { user, token };
    localStorage.setItem(this.storageKey, JSON.stringify(session));
    this._user.set(user);
    this._token.set(token);
  }

  private getFailedAttempts(email: string): number {
    const map = this.readAttempts();
    return map[email] ?? 0;
  }

  private incrementFailedAttempts(email: string): number {
    const map = this.readAttempts();
    map[email] = (map[email] ?? 0) + 1;
    localStorage.setItem(this.attemptsKey, JSON.stringify(map));
    return map[email];
  }

  private clearFailedAttempts(email: string): void {
    const map = this.readAttempts();
    delete map[email];
    localStorage.setItem(this.attemptsKey, JSON.stringify(map));
  }

  private readAttempts(): Record<string, number> {
    try {
      const raw = localStorage.getItem(this.attemptsKey);
      return raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch {
      return {};
    }
  }

  private readSession(): StoredSession | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? (JSON.parse(raw) as StoredSession) : null;
    } catch {
      localStorage.removeItem(this.storageKey);
      return null;
    }
  }
}

function mapAuthToUser(auth: AutenticacionResponse): TaxiUser {
  const name = auth.nombreCompleto?.trim() || auth.username || 'Usuario';
  return {
    name,
    email: auth.username,
    role: auth.role || '',
    avatar: initials(name),
    roles: auth.role ? [auth.role] : [],
    permisos: collectPermisos(auth.permisos, permisosFromJwt(auth.tokenJWT))
  };
}

function mapPerfilToUser(
  perfil: PerfilResponse,
  fallback?: AutenticacionResponse,
  current?: TaxiUser | null
): TaxiUser {
  const fullName = [perfil.nombre, perfil.apellidoPaterno, perfil.apellidoMaterno]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(' ');
  const name = fullName || fallback?.nombreCompleto?.trim() || perfil.correo || 'Usuario';
  const role = perfil.roles?.[0] || fallback?.role || current?.role || '';
  const idUsuario = Number(perfil.idUsuario);

  return {
    name,
    email: perfil.correo || fallback?.username || '',
    role,
    avatar: initials(name),
    idUsuario: Number.isFinite(idUsuario) && idUsuario > 0 ? idUsuario : undefined,
    idPersona: perfil.idPersona,
    roles: perfil.roles ?? (role ? [role] : []),
    estatus: perfil.estatus,
    permisos: collectPermisos(
      perfil.permisos,
      fallback?.permisos,
      current?.permisos,
      permisosFromJwt(fallback?.tokenJWT)
    )
  };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || 'U';
}
